import { useCallback, useState } from "react";

import Typography from "@mui/material/Typography";

import {
  CanOperateInStateQuery,
  CanOperateInStateResult,
  useCanOperateInStateLazyQuery,
} from "@covertree/agent-portal-graphql";
import { RejectedAppointmentInJITStateModal } from "@components/RejectedAppointmentInJITStateModal";
import { AppointmentNotRequestedInNonJITStateModal } from "@components/AppointmentNotRequestedInNonJITStateModal";
import { LackOfLicenseOrAppointmentModal } from "@components/LackOfLicenseOrAppointmentModal";
import { NonInStateModal } from "@components/NotInStateModal";
import { SequoiaInvalidAddressModal } from "@components/SequoiaInvalidAddressModal";
import { MissingAppointmentInNonJITStateModal } from "@components/MissingAppointmentInNonJITStateModal";
import { SurplusLinesInformationModal } from "@covertree/new-business-modals";

import { EffectiveDateExposureAndLapse } from "./EffectiveDateExposureAndLapse/EffectiveDateExposureAndLapse";
import { HomeAddress } from "./HomeAddress/HomeAddress";

export function HomeAddressStep() {
  const [isNotInStateModalOpen, setIsNotInStateModalOpen] =
    useState<boolean>(false);

  const [
    isLackOfLicenseOrAppointmentModalOpen,
    setIsLackOfLicenseOrAppointmentModalOpen,
  ] = useState<boolean>(false);

  const [
    isAppointmentNotRequestedInNonJITStateModalOpen,
    setIsAppointmentNotRequestedInNonJITStateModalOpen,
  ] = useState<boolean>(false);

  const [
    isNotAppointedInNonJITStateModalOpen,
    setIsNotAppointedInNonJITStateModalOpen,
  ] = useState<boolean>(false);

  const [
    isRejectedAppointmentInJITStateModalOpen,
    setIsRejectedAppointmentInJITStateModalOpen,
  ] = useState<boolean>(false);

  const [
    isSequoiaInvalidAddressModalOpen,
    setIsSequoiaInvalidAddressModalOpen,
  ] = useState<boolean>(false);

  const [
    isSurplusLinesInformationModalOpen,
    setIsSurplusLinesInformationModalOpen,
  ] = useState<boolean>(false);

  const [isNextButtonVisible, setIsNextButtonVisible] = useState<boolean>(true);

  const [canOperateInStateQuery, { data }] = useCanOperateInStateLazyQuery();

  const onCanOperateInStateCompleted = useCallback(
    (data: CanOperateInStateQuery) => {
      if (data.canOperateInState === CanOperateInStateResult.Yes) {
        setIsNextButtonVisible(false);
      }

      if (
        !isNotInStateModalOpen &&
        data?.canOperateInState === CanOperateInStateResult.UnsupportedState
      ) {
        setIsNotInStateModalOpen(true);
      }

      if (
        !isLackOfLicenseOrAppointmentModalOpen &&
        data?.canOperateInState === CanOperateInStateResult.MissingLicense
      ) {
        setIsLackOfLicenseOrAppointmentModalOpen(true);
      }

      if (
        !isAppointmentNotRequestedInNonJITStateModalOpen &&
        data?.canOperateInState ===
          CanOperateInStateResult.NotRequestedInNonJitState
      ) {
        setIsAppointmentNotRequestedInNonJITStateModalOpen(true);
      }

      if (
        !isNotAppointedInNonJITStateModalOpen &&
        data?.canOperateInState ===
          CanOperateInStateResult.NotAppointedInNonJitState
      ) {
        setIsNotAppointedInNonJITStateModalOpen(true);
      }

      if (
        !isRejectedAppointmentInJITStateModalOpen &&
        data?.canOperateInState === CanOperateInStateResult.RejectedInJitState
      ) {
        setIsRejectedAppointmentInJITStateModalOpen(true);
      }
    },
    [
      isNotInStateModalOpen,
      isLackOfLicenseOrAppointmentModalOpen,
      isAppointmentNotRequestedInNonJITStateModalOpen,
      isRejectedAppointmentInJITStateModalOpen,
      data?.canOperateInState,
    ]
  );

  return (
    <>
      <Typography variant="h4">Home Address</Typography>
      <HomeAddress
        isNextButtonVisible={isNextButtonVisible}
        setIsNextButtonVisible={setIsNextButtonVisible}
        canOperateInStateQuery={canOperateInStateQuery}
        onCanOperateInStateCompleted={onCanOperateInStateCompleted}
        setIsSequoiaInvalidAddressModalOpen={
          setIsSequoiaInvalidAddressModalOpen
        }
        setIsSurplusLinesInformationModalOpen={
          setIsSurplusLinesInformationModalOpen
        }
      />
      {!isNextButtonVisible &&
      data?.canOperateInState === CanOperateInStateResult.Yes ? (
        <EffectiveDateExposureAndLapse />
      ) : null}

      {/* Modals */}
      <NonInStateModal
        onClose={() => setIsNotInStateModalOpen(false)}
        open={isNotInStateModalOpen}
      />
      <LackOfLicenseOrAppointmentModal
        onClose={() => setIsLackOfLicenseOrAppointmentModalOpen(false)}
        open={isLackOfLicenseOrAppointmentModalOpen}
      />
      <AppointmentNotRequestedInNonJITStateModal
        onClose={() =>
          setIsAppointmentNotRequestedInNonJITStateModalOpen(false)
        }
        open={isAppointmentNotRequestedInNonJITStateModalOpen}
      />
      <MissingAppointmentInNonJITStateModal
        onClose={() => setIsNotAppointedInNonJITStateModalOpen(false)}
        open={isNotAppointedInNonJITStateModalOpen}
      />
      <RejectedAppointmentInJITStateModal
        onClose={() => setIsRejectedAppointmentInJITStateModalOpen(false)}
        open={isRejectedAppointmentInJITStateModalOpen}
      />
      <SequoiaInvalidAddressModal
        open={isSequoiaInvalidAddressModalOpen}
        onClose={() => setIsSequoiaInvalidAddressModalOpen(false)}
      />
      <SurplusLinesInformationModal
        open={isSurplusLinesInformationModalOpen}
        onClose={() => setIsSurplusLinesInformationModalOpen(false)}
      />
    </>
  );
}
